import { Link } from 'react-router-dom';
import { buildSiteHierarchyGroups } from '@/lib/catalogSiteTree';
import { cn } from '@/lib/cn';
import { SiteLevelBadge } from '@/components/SiteLevelBadge';
import type { RobotSiteDisplayRow, RobotSiteRow } from '@/lib/robotSiteTree';
import { useMemo } from 'react';

type Props = {
  rows: RobotSiteDisplayRow[];
  activeSiteId: number;
  linkForSite: (siteId: number) => string;
  queueAggregateById?: Record<number, number>;
  queueDirectById?: Record<number, number>;
};

export function RobotSiteHierarchyChips({
  rows,
  activeSiteId,
  linkForSite,
  queueAggregateById,
  queueDirectById,
}: Props) {
  const sites: RobotSiteRow[] = useMemo(
    () => rows.map((r) => ({ ...r.site, site_code: r.site.site_code })),
    [rows],
  );
  const groups = useMemo(() => buildSiteHierarchyGroups(sites), [sites]);

  if (rows.length === 0) return null;

  return (
    <div className="space-y-2">
      {groups.map((g) => {
        const groupKey = g.parent?.site.id ?? g.children.map((c) => c.site.id).join('-') ?? 'g';
        return (
          <div key={groupKey} className="space-y-1.5">
            {g.parent ? (
              <Level1Chip
                site={g.parent.site}
                active={g.parent.site.id === activeSiteId}
                href={linkForSite(g.parent.site.id)}
                agg={queueAggregateById?.[g.parent.site.id]}
                direct={queueDirectById?.[g.parent.site.id]}
                childCount={g.children.length}
              />
            ) : null}
            {g.children.length > 0 ? (
              <div className={cn(g.parent && 'border-l-2 border-emerald-400/75 pl-3')}>
                <div className="flex flex-wrap gap-2">
                  {g.children.map((child) => (
                    <Level2Chip
                      key={child.site.id}
                      site={child.site}
                      active={child.site.id === activeSiteId}
                      href={linkForSite(child.site.id)}
                      direct={queueDirectById?.[child.site.id]}
                    />
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

function queueLabel(
  active: boolean,
  direct: number | undefined,
  agg: number | undefined,
  childCount: number,
): string | null {
  if (agg != null && direct != null && agg > direct) {
    return `\u961f\u5217 ${agg} \u6761\uff08\u542b\u4e0b\u7ea7 ${agg - direct}\uff09`;
  }
  if (direct != null) {
    return `\u961f\u5217 ${direct} \u6761`;
  }
  if (childCount > 0 && agg != null) {
    return `\u961f\u5217 ${agg} \u6761`;
  }
  return null;
}

function chipLinkClass(active: boolean, isL2: boolean): string {
  return cn(
    'inline-flex max-w-full items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-sm font-medium transition',
    isL2 ? 'text-[13px]' : 'py-2',
    active
      ? 'border-teal-500 bg-teal-50 text-teal-950 shadow-sm ring-2 ring-teal-500/15'
      : 'border-slate-200 bg-white text-slate-700 hover:border-teal-300 hover:bg-slate-50 hover:text-teal-900',
  );
}

function Level1Chip({
  site,
  active,
  href,
  agg,
  direct,
  childCount,
}: {
  site: RobotSiteRow;
  active: boolean;
  href: string;
  agg?: number;
  direct?: number;
  childCount: number;
}) {
  const q = queueLabel(active, direct, agg, childCount);

  return (
    <Link to={href} className={cn(chipLinkClass(active, false), 'w-full max-w-full sm:w-auto')} title={site.site_name}>
      <SiteLevelBadge depth={0} />
      <span className="truncate font-semibold">{site.site_name}</span>
      {q ? <span className={cn('shrink-0 text-xs', active ? 'text-teal-900/85' : 'text-slate-500')}>{q}</span> : null}
      {childCount > 0 ? (
        <span className={cn('shrink-0 text-xs', active ? 'text-teal-800/80' : 'text-slate-400')}>
          {childCount} {'\u4e2a\u4e0b\u7ea7'}
        </span>
      ) : null}
    </Link>
  );
}

function Level2Chip({
  site,
  active,
  href,
  direct,
}: {
  site: RobotSiteRow;
  active: boolean;
  href: string;
  direct?: number;
}) {
  const q = direct != null ? `\u961f\u5217 ${direct}` : null;

  return (
    <Link to={href} className={chipLinkClass(active, true)} title={site.site_name}>
      <SiteLevelBadge depth={1} className="!px-1 !py-px !text-[9px]" />
      <span className="max-w-[8rem] truncate">{site.site_name}</span>
      {q ? <span className={cn('shrink-0 text-[11px]', active ? 'text-teal-900/80' : 'text-slate-500')}>{q}</span> : null}
    </Link>
  );
}
